// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable react-hooks/set-state-in-effect */

import React, { FC, useEffect, useMemo, useState } from "react";
import { Checkbox, Segment, Table, TableCell, Grid, Loader, Popup, Icon } from "semantic-ui-react";
import * as valuePrecisionConverter from "../../../Lumber/ValuePrecisionConverter";

import { std } from "mathjs";
import _ from "lodash";

import { NO_SUMMARY_TABLE_PRESET } from "./utils";
import type { ProtocolAssessmentSelection } from "./ProtocolAnalysisContext";
import type { MeanComparisonLettersByAssessment, ProtocolDataSummaryTableProps } from "./types";
import type { ProtocolSummaryTablePresetSection } from "../../../../apis/types/protocolTypes";

/**
 * ValuePrecisionConverter is untyped JS. getPrecision returns falsy values (null, undefined, 0, NaN)
 * as they are, and anything else rounded to a number.
 */
const getPrecision = valuePrecisionConverter.getPrecision as (
	value: number | null | undefined,
) => number | null | undefined;

/** One entry of the "Global Mean" / "CV" rows, per selected assessment. */
type SummaryMeanRow = {
	assessmentId: string;
	plannedTimingId: string | null;
	unitName: string | null;
	average: number | null | undefined;
	standardDeviation: number | null | undefined;
};

/** A preset section header over the columns it groups. */
type SummarySectionHeader = {
	id: string;
	name: string;
	/** Analysis ids, curve model preset entries and ground data assessment ids, in preset order. */
	assessments: (string | ProtocolSummaryTablePresetSection["presetCurveModels"][number])[];
	colLength: number;
};

const ProtocolDataSummaryTable: FC<ProtocolDataSummaryTableProps> = ({
	compiledData,
	tukeyData,
	updateCheckedTreatment,
	treatmentsChecked,
	selectedAssessmentOptions,
	disableCheckBoxes,
	compiledMeanComparisonsRunning,
	selectedAssessmentPresetOption,
	presetData,
	trialsChecked,
	hideUncheckedTreatmentData,
	hideLetters,
	collapseView = false,
}) => {
	const [tukeyTableData, setTukeyTableData] = useState<MeanComparisonLettersByAssessment | null>(null);
	const [toggleAll, setToggleAll] = useState(true);

	//  reducer for the collapsed view (show only rows which don't exist in treatmentsChecked)
	const { tableData } = useMemo(() => {
		if (!compiledData || !treatmentsChecked) return { tableData: null };
		const checkedTrialTreatmentIds = new Set(
			treatmentsChecked.filter((t) => t.treatmentChecked).map((t) => t.trialTreatmentId),
		);

		if (checkedTrialTreatmentIds.size === compiledData.length) {
			return { tableData: [...compiledData] };
		}

		if (!collapseView) {
			return { tableData: [...compiledData] };
		}

		const filtered = compiledData.filter((t) => checkedTrialTreatmentIds.has(t.trialTreatmentId));
		return { tableData: filtered };
	}, [compiledData, treatmentsChecked, collapseView]);

	//The mean row, across the checked treatments. The per-treatment averages (and their ranking and
	//shading) come in on compiledData, so this only reads them
	const meanData = useMemo((): SummaryMeanRow[] | null => {
		if (!compiledData?.length) return null;
		const checkedRows = _.filter(compiledData, "treatmentChecked");

		return _.map(selectedAssessmentOptions, (sao) => {
			const averages = _.filter(
				_.map(
					checkedRows,
					(row) =>
						_.find(
							row.treatmentAverages,
							(ta) => ta.assessmentId == sao.assessmentId && ta.plannedTimingId == sao.plannedTimingId,
						)?.average,
				),
				(average) => average !== undefined && !isNaN(average),
			);
			const average = _.mean(averages);
			// averages only holds numbers (undefined is filtered out above), but _.filter doesn't narrow
			const standardDeviation = isNaN(average) ? null : (std(averages as number[]) / average) * 100;

			return {
				assessmentId: sao.assessmentId,
				plannedTimingId: sao.plannedTimingId,
				unitName: sao.unitName,
				average: getPrecision(average),
				standardDeviation: getPrecision(standardDeviation),
			};
		});
	}, [compiledData, selectedAssessmentOptions]);

	//Section headers and column order come from the picked preset. "none" means the user is
	//hand-picking, so the columns stay in their checked order with no headers
	const { sectionHeaders, columns } = useMemo((): {
		sectionHeaders: SummarySectionHeader[] | null;
		columns: ProtocolAssessmentSelection[];
	} => {
		const matchingPreset =
			selectedAssessmentPresetOption !== NO_SUMMARY_TABLE_PRESET
				? _.find(presetData, (preset) => preset.id === selectedAssessmentPresetOption)
				: null;
		if (!matchingPreset) return { sectionHeaders: null, columns: selectedAssessmentOptions };

		const headers: SummarySectionHeader[] = [];
		let sortedAssessments: ProtocolAssessmentSelection[] = [];
		_.forEach(_.orderBy(matchingPreset.sections, "sectionOrder"), (section) => {
			if (!section.displaySectionName) return;

			const assessmentsInSection: SummarySectionHeader["assessments"] = [
				..._.map(_.orderBy(section.presetAnalyses, "assessmentOrder"), "analysisId"),
				..._.orderBy(section.presetCurveModels, "assessmentOrder"),
				..._.map(_.orderBy(section.presetGroundData, "assessmentOrder"), "groundDataAssessmentId"),
			];

			const matchingAssessments = _.filter(
				selectedAssessmentOptions,
				(sao) =>
					assessmentsInSection.includes(sao.assessmentId) ||
					_.some(
						assessmentsInSection,
						(presetAssessment) =>
							// A string entry has none of these fields, so it never matches a selection
							typeof presetAssessment !== "string" &&
							presetAssessment.curveModelAnalysisId === sao.curveModelAnalysisId &&
							presetAssessment.analysisId === sao.analysisId &&
							presetAssessment.analysisTypeId === sao.analysisTypeId,
					),
			);
			sortedAssessments = [...sortedAssessments, ...matchingAssessments];

			//Skip headers with no assessments
			if (matchingAssessments.length > 0) {
				headers.push({
					id: section.id,
					name: section.sectionName,
					assessments: assessmentsInSection,
					colLength: matchingAssessments.length,
				});
			}
		});

		return { sectionHeaders: headers, columns: sortedAssessments };
	}, [selectedAssessmentPresetOption, presetData, selectedAssessmentOptions]);

	const displaySectionHeaders = sectionHeaders !== null;

	useEffect(() => {
		if (tukeyData) {
			setTukeyTableData(tukeyData);
		}
	}, [tukeyData]);

	/**
	 * Toggle all treatments to on/off state. Only change state of required records.
	 */
	function toggleAllTreatments() {
		const toggle = !toggleAll;
		setToggleAll(toggle);
		updateCheckedTreatment(
			toggle,
			_.map(
				_.filter(treatmentsChecked, (treatment) => treatment.treatmentChecked !== toggle),
				"trialTreatmentId",
			),
		);
	}

	/**
	 * Calculate new state of "check all" button if treatmentsChecked changes.
	 */
	useEffect(() => {
		const anyUnchecked = treatmentsChecked?.some((tc) => !tc.treatmentChecked);
		setToggleAll(!anyUnchecked);
	}, [treatmentsChecked]);

	return (
		<Segment basic style={{ padding: "0" }}>
			{selectedAssessmentOptions && (
				<>
					<Table celled striped selectable color="blue" textAlign="center" collapsing>
						<Table.Header>
							{displaySectionHeaders && (
								<Table.Row>
									<Table.HeaderCell className="stickyColumn" colSpan={4} />
									{sectionHeaders &&
										_.map(sectionHeaders, (sh) => {
											return (
												<Table.HeaderCell key={sh.id} colSpan={sh.colLength}>
													{sh.name}
												</Table.HeaderCell>
											);
										})}
								</Table.Row>
							)}
							<Table.Row>
								<Table.HeaderCell className="stickyColumn" colSpan={4} textAlign="right">
									Assessment
								</Table.HeaderCell>
								{selectedAssessmentOptions &&
									_.map(columns, (sao, index) => {
										return <Table.HeaderCell key={index}>{sao.assessmentName}</Table.HeaderCell>;
									})}
							</Table.Row>
							<Table.Row>
								<Table.HeaderCell className="stickyColumn" colSpan={4} textAlign="right">
									Planned Timing
								</Table.HeaderCell>
								{selectedAssessmentOptions &&
									_.map(columns, (sao, index) => {
										return <Table.HeaderCell key={index}>{sao.plannedTimingName}</Table.HeaderCell>;
									})}
							</Table.Row>
							<Table.Row>
								<Table.HeaderCell className="stickyColumn" colSpan={4} textAlign="right">
									Normalization
								</Table.HeaderCell>
								{selectedAssessmentOptions &&
									_.map(columns, (sao, index) => {
										return <Table.HeaderCell key={index}>{sao.dataNormalizationName}</Table.HeaderCell>;
									})}
							</Table.Row>
							<Table.Row>
								<Table.HeaderCell className="stickyColumn" colSpan={4} textAlign="right">
									Units
								</Table.HeaderCell>
								{selectedAssessmentOptions &&
									_.map(columns, (sao, index) => {
										return (
											<Table.HeaderCell key={index}>
												{sao.unitName ? sao.unitName : sao.dataNormalizationName?.includes("%") ? "%" : ""}
											</Table.HeaderCell>
										);
									})}
							</Table.Row>
							{/* To prevent stickyColumn collapsing width, the CSS: left is the sum of the previous cell widths */}
							<Table.Row>
								<Table.HeaderCell
									className="stickyColumn"
									style={{ left: 0, width: 120, minWidth: 120, maxWidth: 120 }}
								>
									<Checkbox
										label="Check All"
										checked={toggleAll}
										disabled={disableCheckBoxes}
										onClick={toggleAllTreatments}
									/>
								</Table.HeaderCell>
								<Table.HeaderCell className="stickyColumn" style={{ left: 120, width: 44, minWidth: 44, maxWidth: 44 }}>
									#
								</Table.HeaderCell>
								<Table.HeaderCell
									className="stickyColumn"
									style={{ left: 164, width: 107, minWidth: 107, maxWidth: 107 }}
								>
									Type
								</Table.HeaderCell>
								<Table.HeaderCell className="stickyColumn" style={{ left: 271 }}>
									Name
								</Table.HeaderCell>
								{_.map(columns, (temp, index) => {
									return <Table.HeaderCell colSpan="1" textAlign="center" key={index}></Table.HeaderCell>;
								})}
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{_.map(tableData, (treatment) => {
								return (
									<Table.Row key={treatment.trialTreatmentId}>
										<Table.Cell className="stickyColumn" style={{ left: 0, width: 120, minWidth: 120, maxWidth: 120 }}>
											<Checkbox
												checked={
													_.find(treatmentsChecked, (tc) => tc.trialTreatmentId === treatment.trialTreatmentId)
														?.treatmentChecked
												}
												onChange={() => {
													updateCheckedTreatment(
														!_.find(treatmentsChecked, (tc) => tc.trialTreatmentId === treatment.trialTreatmentId)
															?.treatmentChecked,
														treatment.trialTreatmentId,
													);
												}}
												disabled={disableCheckBoxes}
											/>
										</Table.Cell>
										<Table.Cell className="stickyColumn" style={{ left: 120, width: 44, minWidth: 44, maxWidth: 44 }}>
											{treatment.trialTreatmentId}
										</Table.Cell>
										<Table.Cell
											style={{ left: 164, width: 107, minWidth: 107, maxWidth: 107 }}
											className="stickyColumn"
										>
											{treatment.treatmentTypeName}
										</Table.Cell>
										<Table.Cell
											className="stickyColumn"
											style={{
												left: 271,
												maxWidth: "325px",
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
											textAlign="left"
										>
											<span title={treatment.treatmentName ?? undefined}>{treatment.treatmentName}</span>
											{treatment.hasMissingTreatments ? (
												<span style={{ float: "right" }}>
													<Popup
														trigger={<Icon name="info circle" />}
														content="This treatment is missing from some of the trials on this protocol."
													/>
												</span>
											) : null}
										</Table.Cell>
										{_.map(columns, (options, index) => {
											// TODO(types): assessmentData, the letters and the p-value are looked up by assessmentId only, so a ground data assessment checked at two growth phases shows the first phase's data/letters/p-value in both columns
											return _.some(treatment.assessmentData, (tad) => {
												return tad.assessmentId == options.assessmentId;
											}) ? (
												<Table.Cell
													key={index}
													style={{
														backgroundColor: !_.find(
															treatmentsChecked,
															(tc) => tc.trialTreatmentId === treatment.trialTreatmentId,
														)?.treatmentChecked
															? "lightGray"
															: _.find(treatment.treatmentAverages, (ta) => {
																	return (
																		ta.assessmentId == options.assessmentId &&
																		ta.plannedTimingId == options.plannedTimingId
																	);
																})?.color,
														minWidth: "150px",
													}}
												>
													<Grid style={{ width: "inherit", height: "inherit" }}>
														<Grid.Row style={{ padding: "inherit" }}>
															<Grid.Column width="1" />
															<Grid.Column width="9" style={{ padding: 0 }}>
																{_.every(
																	_.filter(treatment.assessmentData, (tad) => tad.assessmentId == options.assessmentId),
																	(tad) => tad.plots.length > 0,
																) &&
																// Non-null: the _.some above already found a matching entry
																_.find(treatment.assessmentData, (tad) => tad.assessmentId == options.assessmentId)!
																	.assessmentAverage !== null
																	? !_.find(
																			treatmentsChecked,
																			(tc) => tc.trialTreatmentId === treatment.trialTreatmentId,
																		)?.treatmentChecked
																		? ""
																		: getPrecision(
																				_.find(treatment.treatmentAverages, (ta) => {
																					return (
																						ta.assessmentId == options.assessmentId &&
																						ta.plannedTimingId == options.plannedTimingId
																					);
																				})?.average,
																			) +
																			"" +
																			(options.dataNormalizationName?.includes("%") ? "%" : "")
																	: "N/A"}
															</Grid.Column>

															<Grid.Column width="3" style={{ padding: 0 }}>
																{disableCheckBoxes && compiledMeanComparisonsRunning ? (
																	<Loader active size="tiny" />
																) : !_.find(
																		treatmentsChecked,
																		(tc) => tc.trialTreatmentId === treatment.trialTreatmentId,
																  )?.treatmentChecked ||
																  (_.some(trialsChecked, (tc) => !tc.trialChecked) && hideLetters) ||
																  hideLetters ||
																  (_.some(treatmentsChecked, (tc) => !tc.treatmentChecked) &&
																		hideUncheckedTreatmentData) ||
																  hideUncheckedTreatmentData ? (
																	""
																) : (
																	_.find(
																		// TODO(types): assessmentId is never null, so the plannedTimingId fallback is dead
																		tukeyData[options.assessmentId ?? options.plannedTimingId],
																		(td) => td.trialTreatmentId === treatment.trialTreatmentId,
																	)?.tukeyLetters
																)}
															</Grid.Column>
														</Grid.Row>
													</Grid>
												</Table.Cell>
											) : (
												<Table.Cell key={index} style={{ backgroundColor: "lightGray", minWidth: "150px" }}>
													{_.find(treatment.assessmentDataForStrikeThrough, (tad) => {
														return tad.assessmentId == options.assessmentId;
													})?.assessmentAverage !== null
														? !_.find(treatmentsChecked, (tc) => tc.trialTreatmentId === treatment.trialTreatmentId)
																?.treatmentChecked
															? ""
															: getPrecision(
																	_.find(treatment.treatmentAverages, (ta) => {
																		return (
																			ta.assessmentId == options.assessmentId &&
																			ta.plannedTimingId == options.plannedTimingId
																		);
																	})?.averageForStrikeThrough,
																) +
																"" +
																(options.dataNormalizationName?.includes("%") ? "%" : "")
														: "N/A"}
												</Table.Cell>
											);
										})}
									</Table.Row>
								);
							})}
							<Table.Row>
								<Table.Cell className="stickyColumn" textAlign="right" colSpan={4}>
									CV
								</Table.Cell>
								{_.map(columns, (sao, index) => {
									return _.find(meanData, (md) => {
										return sao.assessmentId == md.assessmentId && md.plannedTimingId == sao.plannedTimingId;
									})?.standardDeviation ? (
										<Table.Cell key={index}>
											{(_.some(treatmentsChecked, (tc) => !tc.treatmentChecked) && hideUncheckedTreatmentData) ||
											hideUncheckedTreatmentData ||
											(_.some(trialsChecked, (tc) => !tc.trialChecked) && hideLetters) ||
											hideLetters
												? ""
												: Math.abs(
														_.meanBy(
															_.map(
																_.filter(meanData, (tad) => {
																	return (
																		tad.assessmentId == sao.assessmentId && tad.plannedTimingId == sao.plannedTimingId
																	);
																}),
																"standardDeviation",
															),
														),
													)}
											{(_.some(treatmentsChecked, (tc) => !tc.treatmentChecked) && hideUncheckedTreatmentData) ||
											hideUncheckedTreatmentData ||
											(_.some(trialsChecked, (tc) => !tc.trialChecked) && hideLetters) ||
											hideLetters
												? ""
												: "%"}
										</Table.Cell>
									) : (
										<TableCell key={index} style={{ backgroundColor: "lightGray" }}>
											N/A
										</TableCell>
									);
								})}
							</Table.Row>
							<Table.Row>
								<Table.Cell className="stickyColumn" textAlign="right" colSpan={4}>
									P-Value
								</Table.Cell>
								{_.map(columns, (sao, index) => {
									// TODO(types): keyed by assessmentId only, so two growth phases of one ground data assessment show the same p-value
									return tukeyTableData && (tukeyTableData[sao.assessmentId]?.length ?? 0) > 0 ? (
										<Table.Cell key={index}>
											{(_.some(treatmentsChecked, (tc) => !tc.treatmentChecked) && hideUncheckedTreatmentData) ||
											hideUncheckedTreatmentData ||
											(_.some(trialsChecked, (tc) => !tc.trialChecked) && hideLetters) ||
											hideLetters
												? ""
												: Number(Number(tukeyTableData[sao.assessmentId][0]?.pValue).toFixed(5)) === 0
													? "<0.00001"
													: Number(Number(tukeyTableData[sao.assessmentId][0]?.pValue).toFixed(5))}
										</Table.Cell>
									) : (
										<Table.Cell key={index} style={{ backgroundColor: "lightGray" }}>
											N/A
										</Table.Cell>
									);
								})}
							</Table.Row>
						</Table.Body>
					</Table>
				</>
			)}
		</Segment>
	);
};

export default ProtocolDataSummaryTable;
